import type { Key } from 'react';
import { useId, useState } from 'react';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'react-aria-components/Tabs';
import { cx } from 'class-variance-authority';

import styles from './styles/Carousel.module.css';

export interface MediaItem {
	/** Image source shown in light mode (and in dark mode when `darkSrc` is omitted). */
	src: string;
	/** Accessible alt text for the image. */
	alt: string;
	/** Optional image source used in dark mode (`[data-theme='dark']`). */
	darkSrc?: string;
	/** Optional visible caption rendered beneath the image (per-slide in the carousel). */
	caption?: string;
}

/**
 * Renders the slide image. When `darkSrc` is set, both variants render and CSS swaps them via
 * `[data-theme='dark']`; otherwise a single image renders with no swap class so `src` shows in both
 * themes (the dark-mode fallback documented on `MediaItem.src`).
 */
const SlideImage = ({ item }: { item: MediaItem }) =>
	item.darkSrc ? (
		<>
			<img className={cx(styles.image, styles.imageLight)} src={item.src} alt={item.alt} />
			<img className={cx(styles.image, styles.imageDark)} src={item.darkSrc} alt={item.alt} />
		</>
	) : (
		<img className={styles.image} src={item.src} alt={item.alt} />
	);

export interface CarouselProps {
	/**
	 * Media to display. A single item renders a static image; more than one renders an auto-advancing,
	 * keyboard-navigable carousel.
	 */
	media: MediaItem[];
}

/**
 * A media carousel. Renders a single static image when `media` has one item, or an auto-advancing
 * (fixed 7s), keyboard-navigable carousel when it has more.
 *
 * Built on React Aria `Tabs`: the dot navigation is a `TabList`/`Tab` and the slides are `TabPanel`s
 * inside `TabPanels`, so keyboard, focus, roving tabindex, ARIA, and the entering/exiting crossfade
 * lifecycle (`data-entering`/`data-exiting`) all come from RAC rather than being hand-rolled.
 *
 * Auto-advance is a pure-CSS clock, so the component holds a single piece of state (the active index)
 * and no effects or timers: an invisible element runs a fixed keyframe animation and `onAnimationEnd`
 * advances the slide. Hover/focus pause it and `prefers-reduced-motion` disables it — both in CSS.
 *
 * https://react-aria.adobe.com/Tabs.html
 */
export const Carousel = ({ media }: CarouselProps) => {
	const [activeIndex, setActiveIndex] = useState(0);
	const captionId = useId();

	if (media.length === 0) {
		return null;
	}

	const isCarousel = media.length > 1;
	const hasCaptions = media.some((item) => Boolean(item.caption));

	if (!isCarousel) {
		const item = media[0];

		return (
			<div className={styles.gallery}>
				<div className={styles.imageContainer}>
					<SlideImage item={item} />
				</div>
				{item.caption ? <p className={styles.caption}>{item.caption}</p> : null}
			</div>
		);
	}

	const advance = () => {
		setActiveIndex((index) => (index + 1) % media.length);
	};

	return (
		<div className={styles.gallery}>
			<Tabs selectedKey={String(activeIndex)} onSelectionChange={(key: Key) => setActiveIndex(Number(key))}>
				<TabPanels className={styles.imageContainer}>
					{media.map((item, index) => (
						<TabPanel
							key={index}
							id={String(index)}
							className={styles.slide}
							aria-describedby={index === activeIndex && item.caption ? captionId : undefined}
						>
							<SlideImage item={item} />
						</TabPanel>
					))}
				</TabPanels>
				{hasCaptions ? (
					<p id={captionId} className={styles.caption}>
						{media[activeIndex]?.caption ?? ''}
					</p>
				) : null}
				<TabList className={styles.dots} aria-label="Carousel navigation">
					{media.map((item, index) => (
						<Tab
							key={index}
							id={String(index)}
							className={styles.dot}
							aria-label={`Show image ${index + 1}: ${item.alt}`}
						/>
					))}
				</TabList>
				{/*
				 * Auto-advance clock: an invisible element runs a fixed CSS animation and advances the slide
				 * when it ends. Keying it to the active index remounts it each slide so the countdown restarts.
				 * Hover/focus pause it and reduced-motion disables it, both in CSS — no timers or media hooks.
				 */}
				<div key={activeIndex} className={styles.autoAdvanceTimer} onAnimationEnd={advance} aria-hidden="true" />
			</Tabs>
		</div>
	);
};
